import axios from './axios';
import { buildUrl } from '../constants/apiEndpoints';
import { parseApiError } from '../common/errors/api-error';

/**
 * API Client wrapper for making HTTP requests
 */
class APIClient {
    /**
     * GET request
     * @param {string} endpoint - API endpoint
     * @param {object} params - URL parameters
     * @param {object} config - Axios config
     */
    async get(endpoint, params = {}, config = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.get(url, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * POST request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request body
     * @param {object} params - URL parameters
     * @param {object} config - Axios config
     */
    async post(endpoint, data = {}, params = {}, config = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.post(url, data, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * PUT request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request body
     * @param {object} params - URL parameters
     * @param {object} config - Axios config
     */
    async put(endpoint, data = {}, params = {}, config = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.put(url, data, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * PATCH request
     * @param {string} endpoint - API endpoint
     * @param {object} data - Request body
     * @param {object} params - URL parameters
     * @param {object} config - Axios config
     */
    async patch(endpoint, data = {}, params = {}, config = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.patch(url, data, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * DELETE request
     * @param {string} endpoint - API endpoint
     * @param {object} params - URL parameters
     * @param {object} config - Axios config
     */
    async delete(endpoint, params = {}, config = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.delete(url, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Upload file
     * @param {string} endpoint - API endpoint
     * @param {FormData} formData - Form data with file
     * @param {object} params - URL parameters
     * @param {function} onUploadProgress - Progress callback
     */
    async upload(endpoint, formData, params = {}, onUploadProgress = null) {
        try {
            const url = buildUrl(endpoint, params);
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            };

            if (onUploadProgress) {
                config.onUploadProgress = onUploadProgress;
            }

            const response = await axios.post(url, formData, config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Download file
     * @param {string} endpoint - API endpoint
     * @param {object} params - URL parameters
     * @param {string} filename - File name for download
     */
    async download(endpoint, params = {}, filename = 'download') {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.get(url, {
                responseType: 'blob',
            });

            // Create download link
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return { success: true };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Fetch a file as a blob and return an in-app object URL for preview.
     * The caller is responsible for revoking the URL with URL.revokeObjectURL.
     * @param {string} endpoint - API endpoint
     * @param {object} params - URL parameters
     */
    async getBlobUrl(endpoint, params = {}) {
        try {
            const url = buildUrl(endpoint, params);
            const response = await axios.get(url, { responseType: 'blob' });
            return { url: window.URL.createObjectURL(response.data) };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors - returns unified ApiError (code-based, not status-based)
     * Backward compatible: ApiError has .message, .statusCode, .code, .details and .data alias
     * @param {Error} error - Axios error
     * @returns {import('../common/errors/api-error').ApiError}
     */
    handleError(error) {
        const apiError = parseApiError(error);
        // Back-compat aliases expected by legacy callers (status, data)
        apiError.status = apiError.statusCode;
        apiError.data = error.response?.data;
        return apiError;
    }
}

// Create singleton instance
const apiClient = new APIClient();

export default apiClient;
